import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner } from "@/components/ui/Spinner";
import { MeetingRoomView } from "../components/MeetingRoomView";
import { useActiveMeeting } from "../components/MeetingRoomProvider";
import { useMeetingDockStore } from "../store/meetingDockStore";

/**
 * Thin full-screen entry for a meeting. The live connection is owned by
 * MeetingRoomProvider (mounted in ProtectedLayout) so it survives navigation;
 * this route just asks the provider to open the meeting full-screen and renders
 * its view. Navigating away collapses the meeting into the dock instead of
 * dropping the call.
 */
export function MeetingRoomPage() {
  const { occurrenceId } = useParams<{ occurrenceId: string }>();
  const navigate = useNavigate();
  const enterFull = useMeetingDockStore((s) => s.enterFull);
  const dock = useMeetingDockStore((s) => s.dock);
  const leaveStore = useMeetingDockStore((s) => s.leave);
  const mode = useMeetingDockStore((s) => s.mode);
  const active = useActiveMeeting();

  useEffect(() => {
    if (occurrenceId) enterFull(occurrenceId);
  }, [occurrenceId, enterFull]);

  // Leaving the route (a link, back button…) collapses the call into the dock
  // rather than tearing it down — the user keeps the meeting while they browse.
  useEffect(() => {
    return () => {
      const s = useMeetingDockStore.getState();
      if (s.occurrenceId === occurrenceId && s.mode === "full") s.dock();
    };
  }, [occurrenceId]);

  const detailUrl = `/meetings/occurrences/${occurrenceId}`;

  const onDock = () => {
    dock();
    navigate(detailUrl);
  };

  const onLeave = () => {
    active?.room.leave();
    leaveStore();
    navigate(detailUrl);
  };

  const connecting =
    !active || active.occurrenceId !== occurrenceId || mode !== "full";

  return (
    <div className="-m-4 flex h-[calc(100%+2rem)] flex-col lg:-m-6 lg:h-[calc(100%+3rem)]">
      {connecting ? (
        <div className="flex h-full items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <MeetingRoomView
          occurrenceId={occurrenceId!}
          room={active.room}
          devices={active.devices}
          variant="full"
          onDock={onDock}
          onExpand={() => {}}
          onLeave={onLeave}
        />
      )}
    </div>
  );
}
